import { useMemo } from "react";
import { Box, Text } from "@radix-ui/themes";
import { useSecureFile } from "@/hooks/useSecureFile";
import { DashboardWidget } from "@/types/breeze/Dashboard";
import { resolveMediaCategory } from "@/utils/fileUtils";

interface Props {
  widget: DashboardWidget;
}

type MediaKind = "video" | "audio" | "iframe" | "image";

export function MediaWidget({ widget }: Props) {
  const config = parseConfig(widget.config_json);
  const fileId =
    config?.source === "file" && typeof config?.fileId === "number"
      ? config.fileId
      : null;
  const { blobUrl, loading, error } = useSecureFile(fileId);

  const { src, kind } = useMemo(() => {
    if (config?.source === "file") {
      const fileKind = fileKindFromMime(config?.fileType) ?? guessKindFromName(config?.fileName ?? "");
      return { src: fileKind ? blobUrl : null, kind: fileKind };
    }
    if (config?.source === "external" && config?.url) {
      const embed = toEmbedUrl(config.url);
      return embed;
    }
    return { src: null, kind: null };
  }, [config?.source, config?.url, config?.fileName, config?.fileType, blobUrl]);

  return (
    <Box p="4" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Text size="1" color="gray" mb="2" asChild>
        <div>{widget.title}</div>
      </Text>
      <Box style={{ flex: 1, position: "relative", minHeight: 0 }}>
        {loading && <Text size="2" color="gray">Loading media…</Text>}
        {error && <Text size="2" color="red">{error}</Text>}
        {!loading && !error && !src && (
          <Text size="2" color="gray">
            {config?.source ? "No media selected" : "Configure this widget to pick media"}
          </Text>
        )}
        {src && kind === "iframe" && (
          <iframe
            src={src}
            title={widget.title}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            style={{
              width: "100%",
              height: "100%",
              border: 0,
              borderRadius: "var(--radius-2)",
            }}
          />
        )}
        {src && kind === "video" && (
          <video
            src={src}
            controls
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: "var(--radius-2)",
              backgroundColor: "black",
            }}
          />
        )}
        {src && kind === "audio" && (
          <audio
            src={src}
            controls
            style={{ width: "100%", marginTop: "var(--space-2)" }}
          />
        )}
        {src && kind === "image" && (
          <img
            src={src}
            alt={widget.title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: "var(--radius-2)",
            }}
          />
        )}
      </Box>
    </Box>
  );
}

// ── URL → embed resolution ──────────────────────────────────────────────────

function toEmbedUrl(raw: string): { src: string; kind: MediaKind } {
  const url = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;

  // YouTube: youtu.be/ID, youtube.com/watch?v=ID, youtube.com/shorts/ID
  const yt = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([A-Za-z0-9_-]{6,})/,
  );
  if (yt) return { src: `https://www.youtube.com/embed/${yt[1]}`, kind: "iframe" };

  // Vimeo: vimeo.com/ID
  const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return { src: `https://player.vimeo.com/video/${vim[1]}`, kind: "iframe" };

  // Loom: loom.com/share/ID
  const loom = url.match(/loom\.com\/share\/([A-Za-z0-9]+)/);
  if (loom) return { src: `https://www.loom.com/embed/${loom[1]}`, kind: "iframe" };

  const kind = guessKindFromName(url) ?? "video";
  return { src: url, kind };
}

function fileKindFromMime(mime: string | undefined): MediaKind | null {
  if (!mime) return null;
  const category = resolveMediaCategory({ file_type: mime });
  if (category === "image") return "image";
  if (category === "video") return "video";
  if (category === "audio") return "audio";
  return null;
}

function guessKindFromName(name: string): MediaKind | null {
  const lower = name.toLowerCase();
  if (/\.(mp3|wav|m4a|aac|flac|ogg|oga)(\?|$)/.test(lower)) return "audio";
  if (/\.(png|jpe?g|gif|webp|svg|avif|bmp|ico)(\?|$)/.test(lower)) return "image";
  if (/\.(mp4|webm|mov|mkv|ogv)(\?|$)/.test(lower)) return "video";
  return null;
}

function parseConfig(configJson: string | null): {
  source?: "file" | "external";
  fileId?: number;
  fileName?: string;
  fileType?: string;
  url?: string;
} | null {
  if (!configJson) return null;
  try {
    return JSON.parse(configJson);
  } catch {
    return null;
  }
}
